import { useMutation, useQuery } from '@apollo/client';
import DocsIcon from '@clearkit/icons/glyphs/Docs';
import CircularProgress from '@material-ui/core/CircularProgress';
import { CKBox, CKButton, CKField, CKTextField } from 'clearkit';
import { useEffect, useState } from 'react';

import SecurityDisclaimer from '~/components/ConfigurationSecurityDisclaimer';

import query from './query.graphql';
import mutation from './upsert.graphql';

const learnMoreLink =
  'https://help.clearbit.com/hc/en-us/articles/4420189078807';

export default function MarketoConnectionForm({ onSuccess }) {
  const [clientId, setClientId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [endpointUrl, setEndpointUrl] = useState(null);

  const { data = {}, loading } = useQuery(query);
  const [upsertMarketoConnector, { loading: upsertLoading }] = useMutation(
    mutation,
  );

  const [updateErrors, setUpdateErrors] = useState({});
  const { accountConnector = null } = data;

  useEffect(() => {
    if (accountConnector) {
      setClientId(accountConnector.clientId);
      setClientSecret(accountConnector.clientSecret);
      setEndpointUrl(accountConnector.endpointUrl);
    }
  }, [accountConnector]);

  async function upsertMarketoAccountConnector() {
    setUpdateErrors({});

    const { data: mutationData } = await upsertMarketoConnector({
      variables: {
        input: {
          clientId,
          clientSecret,
          endpointUrl,
        },
      },
    });

    let { errors } = mutationData.upsertMarketoConnector;

    if (errors.length > 0) {
      const errorObj = {};

      errors.forEach(({ field, messages }) => {
        errorObj[field] = messages;
      });

      setUpdateErrors(errorObj);
    } else {
      onSuccess();
    }
  }

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <CKBox className="p-8">
        <div className="text-lg font-semibold text-center text-black">
          Connect to your Marketo account
        </div>

        <p className="pb-4 text-sm text-center text-gray-500">
          Connect your Marketo account to sync Clearbit data to Marketo.
          <a
            href={learnMoreLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex gap-1">
            <DocsIcon />
            How to Connect to Marketo
          </a>
        </p>

        <div className="flex flex-col pb-4 gap-2">
          <div className="flex justify-between text-sm">
            <label htmlFor="client-id">Client ID</label>
          </div>

          <CKField variant={updateErrors.client_id ? 'error' : null}>
            <CKTextField
              className="block w-full"
              id="client-id"
              value={clientId || ''}
              onChange={(e) => {
                const value = e.currentTarget.value.trim();
                setClientId(value);
              }}
            />
          </CKField>
        </div>

        <div className="flex flex-col pb-4 gap-2">
          <div className="flex justify-between text-sm">
            <label htmlFor="client-secret">Client Secret</label>
          </div>

          <CKField variant={updateErrors.client_secret ? 'error' : null}>
            <CKTextField
              className="block w-full"
              id="client-secret"
              value={clientSecret || ''}
              onChange={(e) => {
                const value = e.currentTarget.value.trim();
                setClientSecret(value);
              }}
            />
          </CKField>
        </div>

        <div className="flex flex-col pb-4 gap-2">
          <div className="flex justify-between text-sm">
            <label htmlFor="endpoint-url">Endpoint URL</label>
          </div>

          <CKField variant={updateErrors.endpoint_url ? 'error' : null}>
            <CKTextField
              className="block w-full"
              id="endpoint-url"
              value={endpointUrl || ''}
              placeholder="example https://123-ABC-456.mktorest.com"
              onChange={(e) => {
                const value = e.currentTarget.value.trim();
                setEndpointUrl(value);
              }}
            />
          </CKField>
        </div>

        <CKButton
          onClick={upsertMarketoAccountConnector}
          isDisabled={!clientId || !clientSecret || !endpointUrl}
          isLoading={upsertLoading}
          variant="bold"
          variantColor="blue">
          Connect Marketo
        </CKButton>
      </CKBox>
      <SecurityDisclaimer className="pt-4" />
    </>
  );
}
